import { API_ENDPOINTS, buildApiActionUrl } from '../utils/apiConfig';

export interface DokumentEntry {
  id: string;
  filename: string;
  title: string;
  description: string;
  uploadedAt: number;
  uploadedBy: string;
  filesize: number;
}

export interface DokumentResponse {
  success: boolean;
  message?: string;
  documents?: DokumentEntry[];
  updatedAt?: number;
  error?: string;
  document?: DokumentEntry;
}

const API_BASE = API_ENDPOINTS.api;

const dokumenteService = {
  /**
   * Holt alle Dokumente vom Server
   */
  async getDocuments(token: string): Promise<{ documents: DokumentEntry[]; updatedAt: number }> {
    try {
      const response = await fetch(buildApiActionUrl('getDocuments'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      return {
        documents: data.documents || [],
        updatedAt: data.updatedAt || Date.now()
      };
    } catch (error) {
      throw error instanceof Error ? error : new Error('Unbekannter Fehler beim Abrufen der Dokumente');
    }
  },

  /**
   * Lädt eine PDF-Datei hoch und aktualisiert Metadaten
   */
  async uploadDocument(
    file: File,
    title: string,
    description: string,
    token: string
  ): Promise<DokumentEntry> {
    if (file.type !== 'application/pdf') {
      throw new Error('Nur PDF-Dateien sind erlaubt');
    }

    if (file.size > 50 * 1024 * 1024) {
      // 50 MB limit
      throw new Error('Dateigrößenlimit überschritten (max. 50 MB)');
    }

    const formData = new FormData();
    formData.append('action', 'uploadDocument');
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);

    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || data.message || 'Upload fehlgeschlagen');
      }

      if (!data.document) {
        throw new Error('Server hat keine Dokument-Metadaten zurückgegeben');
      }

      return data.document;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Fehler beim Upload der Datei');
    }
  },

  /**
   * Aktualisiert Metadaten (Titel, Beschreibung) eines Dokuments
   */
  async updateDocument(
    documentId: string,
    title: string,
    description: string,
    token: string
  ): Promise<DokumentEntry> {
    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateDocument',
          id: documentId,
          title,
          description
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || data.message || 'Update fehlgeschlagen');
      }

      if (!data.document) {
        throw new Error('Server hat keine Dokument-Metadaten zurückgegeben');
      }

      return data.document;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Fehler beim Aktualisieren des Dokuments');
    }
  },

  /**
   * Löscht ein Dokument (nur Admin)
   */
  async deleteDocument(documentId: string, token: string): Promise<void> {
    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'deleteDocument',
          id: documentId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || data.message || 'Löschen fehlgeschlagen');
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error('Fehler beim Löschen des Dokuments');
    }
  },

  /**
   * Sucht in Dokumenten (Filename, Titel, Beschreibung)
   */
  searchDocuments(documents: DokumentEntry[], query: string): DokumentEntry[] {
    if (!query.trim()) {
      return documents;
    }

    const lowerQuery = query.trim().toLowerCase();
    return documents.filter(doc => {
      const filename = (doc.filename || '').toLowerCase();
      const title = (doc.title || '').toLowerCase();
      const description = (doc.description || '').toLowerCase();

      return filename.includes(lowerQuery) || title.includes(lowerQuery) || description.includes(lowerQuery);
    });
  },

  /**
   * Sortiert Dokumente nach Titel oder Dateiname
   */
  sortDocuments(documents: DokumentEntry[], sortBy: 'title' | 'filename' | 'date'): DokumentEntry[] {
    const sorted = [...documents];

    if (sortBy === 'title') {
      sorted.sort((a, b) => (a.title || a.filename).localeCompare(b.title || b.filename, 'de'));
    } else if (sortBy === 'filename') {
      sorted.sort((a, b) => a.filename.localeCompare(b.filename, 'de'));
    } else if (sortBy === 'date') {
      sorted.sort((a, b) => b.uploadedAt - a.uploadedAt);
    }

    return sorted;
  }
};

export default dokumenteService;
